import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import Icon from '../components/ui/Icon';
import { EmptyState } from '../components/ui/States';
import FuelPriceCard from '../components/onboarding/FuelPriceCard';
import SavingsPreview from '../components/onboarding/SavingsPreview';
import { getFuelMeta } from '../components/onboarding/fuelTypes';
import { toDraft, toPayload, validateDraft } from '../components/onboarding/priceForm';
import { getOnboardingPrices, updateOnboardingPrice } from '../api/onboardingPrices';
import { ApiError } from '../api/client';

const errorMessage = (err) => (err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');

function SectionLabel({ children, aside }) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 bg-surface-container-high/60 rounded-lg text-on-surface-variant text-[11px] leading-[14px]">
      <span className="tracking-wide uppercase font-semibold">{children}</span>
      {aside}
    </div>
  );
}

function CardsSkeleton() {
  return (
    <div className="flex flex-col gap-3" role="status" aria-label="Loading fuel prices">
      {Array.from({ length: 4 }, (_, i) => (
        <div
          key={i}
          className="bg-surface-container-lowest rounded-xl p-5 border border-outline-variant/30 animate-pulse flex items-center gap-4"
        >
          <div className="w-12 h-12 rounded-xl bg-surface-container shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-40 bg-surface-container rounded" />
            <div className="h-3 w-56 bg-surface-container-low rounded" />
          </div>
          <div className="hidden md:flex gap-3 flex-1">
            <div className="h-10 flex-1 bg-surface-container-low rounded-lg" />
            <div className="h-10 flex-1 bg-surface-container-low rounded-lg" />
            <div className="h-10 flex-1 bg-surface-container-low rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function OnboardingConfigPage() {
  const [prices, setPrices] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [loadError, setLoadError] = useState('');
  const [drafts, setDrafts] = useState({}); // id → { stationPrice, pompiPrice, sizePerFillUp } as strings
  const [errors, setErrors] = useState({}); // id → { field: message }
  const [attempted, setAttempted] = useState({}); // id → true after the first Update click
  const [saving, setSaving] = useState({}); // id → true while the PUT is in flight
  const [serverErrors, setServerErrors] = useState({}); // id → message
  const [previewId, setPreviewId] = useState(null);

  const load = useCallback(async () => {
    setStatus('loading');
    setLoadError('');
    try {
      const data = await getOnboardingPrices();
      setPrices(data);
      setDrafts(Object.fromEntries(data.map((p) => [p.id, toDraft(p)])));
      setErrors({});
      setAttempted({});
      setServerErrors({});
      setPreviewId((current) => (data.some((p) => p.id === current) ? current : data[0]?.id ?? null));
      setStatus('ready');
    } catch (err) {
      setLoadError(errorMessage(err));
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (price, field, value) => {
    const next = { ...drafts[price.id], [field]: value };
    setDrafts((d) => ({ ...d, [price.id]: next }));
    setServerErrors((e) => ({ ...e, [price.id]: '' }));
    if (attempted[price.id]) setErrors((e) => ({ ...e, [price.id]: validateDraft(next, price.unit) }));
    setPreviewId(price.id);
  };

  const handleReset = (price) => {
    setDrafts((d) => ({ ...d, [price.id]: toDraft(price) }));
    setErrors((e) => ({ ...e, [price.id]: {} }));
    setAttempted((a) => ({ ...a, [price.id]: false }));
    setServerErrors((e) => ({ ...e, [price.id]: '' }));
  };

  const handleUpdate = async (price) => {
    const draft = drafts[price.id];
    const fieldErrors = validateDraft(draft, price.unit);
    setAttempted((a) => ({ ...a, [price.id]: true }));
    setErrors((e) => ({ ...e, [price.id]: fieldErrors }));
    setServerErrors((e) => ({ ...e, [price.id]: '' }));

    const firstInvalid = Object.keys(fieldErrors)[0];
    if (firstInvalid) {
      const suffix = { stationPrice: 'station', pompiPrice: 'pompi', sizePerFillUp: 'size' }[firstInvalid];
      document.getElementById(`fuel-${price.id}-${suffix}`)?.focus();
      return;
    }

    setSaving((s) => ({ ...s, [price.id]: true }));
    try {
      const { message, price: updated } = await updateOnboardingPrice(price.id, toPayload(draft));
      // Keep the row's place in the list; trust the server's recalculated values
      setPrices((list) => list.map((p) => (p.id === price.id ? { ...p, ...updated } : p)));
      setDrafts((d) => ({ ...d, [price.id]: toDraft({ ...price, ...updated }) }));
      setAttempted((a) => ({ ...a, [price.id]: false }));
      toast.success(message || `${getFuelMeta(price.fuelType).name} prices updated`);
    } catch (err) {
      const message = errorMessage(err);
      setServerErrors((e) => ({ ...e, [price.id]: message }));
      toast.error(message);
    } finally {
      setSaving((s) => ({ ...s, [price.id]: false }));
    }
  };

  return (
    <>
      <PageHeader title="Onboarding Fuel Configuration" />

      <div className="bg-surface-container-low rounded-xl px-5 py-3 flex items-center gap-2 shadow-sm">
        <Icon name="info" size={20} className="text-primary-container" />
        <p className="text-xs text-on-surface">
          Changes take effect immediately on new onboarding sessions. Existing active user profiles retain historical
          benchmark data until their next route recalculation.
        </p>
      </div>

      {status === 'error' && (
        <section className="bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs">
          <EmptyState
            tone="alert"
            icon="cloud_off"
            title="Couldn't load fuel prices"
            description={loadError}
            action={
              <Button icon="refresh" onClick={load}>
                Try again
              </Button>
            }
          />
        </section>
      )}

      {status !== 'error' && (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 items-start">
          <div className="xl:col-span-8 flex flex-col gap-3">
            <SectionLabel
              aside={
                status === 'ready' && (
                  <span className="font-data-mono text-xs">
                    {prices.length} Engine {prices.length === 1 ? 'Category' : 'Categories'}
                  </span>
                )
              }
            >
              Fuel Classification &amp; Benchmark Parameters
            </SectionLabel>

            {status === 'loading' && <CardsSkeleton />}

            {status === 'ready' && prices.length === 0 && (
              <section className="bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs">
                <EmptyState
                  icon="local_gas_station"
                  title="No fuel types configured"
                  description="The backend returned no onboarding benchmark prices."
                  action={
                    <Button variant="secondary" icon="refresh" onClick={load}>
                      Reload
                    </Button>
                  }
                />
              </section>
            )}

            {status === 'ready' &&
              prices.map((price) => (
                <FuelPriceCard
                  key={price.id}
                  price={price}
                  draft={drafts[price.id]}
                  errors={errors[price.id] ?? {}}
                  saving={Boolean(saving[price.id])}
                  serverError={serverErrors[price.id]}
                  onChange={(field, value) => handleChange(price, field, value)}
                  onReset={() => handleReset(price)}
                  onUpdate={() => handleUpdate(price)}
                />
              ))}
          </div>

          <div className="xl:col-span-4 flex flex-col gap-3 xl:sticky xl:top-20">
            <SectionLabel>App Screen Simulation</SectionLabel>
            {status === 'loading' && (
              <div className="bg-surface-container-lowest rounded-2xl border border-outline-variant/30 h-[560px] animate-pulse" />
            )}
            {status === 'ready' && prices.length > 0 && (
              <SavingsPreview prices={prices} drafts={drafts} activeId={previewId} onSelect={setPreviewId} />
            )}
          </div>
        </div>
      )}
    </>
  );
}
